import { Separator } from "@/components/ui/separator";
import { useLanguage } from "@/contexts/LanguageContext";
import { translations } from "@/translations";
import instagramQr from "@/assets/instagram-qr.jpeg";
import whatsappChannelQr from "@/assets/whatsapp-channel-qr.jpeg";
import logo from "@/assets/arabian-alwaha-logo.jpg";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Globe, MessageCircle, Linkedin, Youtube } from "lucide-react";
import { QRCode } from "react-qr-code";

const Footer = () => {
  const { language } = useLanguage();
  const t = translations[language];
  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="container mx-auto px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="relative w-14 h-14 rounded-lg overflow-visible shadow-soft bg-white p-1 border-2 border-accent/30 shrink-0">
                <img
                  src={logo}
                  alt="Arabian Al Waha Est Logo"
                  className="w-full h-full object-contain rounded-md"
                  loading="lazy"
                  width="640"
                  height="640"
                />
                <span
                  aria-label="Trademark"
                  title="Trademark"
                  className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-accent text-primary-darker text-[9px] font-bold leading-none shadow ring-1 ring-primary-darker/30 select-none"
                >
                  ™
                </span>
              </div>
              <h3 className="text-2xl font-bold text-accent">
                Arabian Al Waha Est
                <sup
                  aria-label="Trademark"
                  className="ms-0.5 text-[0.5em] align-super"
                >
                  ™
                </sup>
              </h3>
            </div>
            <p className="text-sm text-primary-foreground/70 mb-4 italic">{t.formerlyKnown}</p>
            <p className="text-primary-foreground/80 leading-relaxed mb-6 max-w-lg">
              {t.aboutDescription}
            </p>
            <div className="text-sm text-primary-foreground/60">
              <p>{t.location}</p>
              <p>{t.footerTagline}</p>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold mb-4 text-accent">{t.ourServices}</h4>
            <ul className="space-y-2 text-primary-foreground/80">
              <li>{t.contractingTitle}</li>
              <li>{t.tradingTitle}</li>
              <li>{t.equipmentTitle}</li>
              <li>{t.vehicleRental}</li>
              <li>{t.workforceTitle}</li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold mb-4 text-accent">{t.contact}</h4>
            <ul className="space-y-2 text-primary-foreground/80">
              <li>
                <a href="tel:+966504838870" className="hover:text-accent transition-colors">
                  <bdi dir="ltr" className="font-mono">+966 50 483 8870</bdi>
                </a>
              </li>
              <li>
                <a href="mailto:sales@arabianalwaha.com" className="hover:text-accent transition-colors">
                  sales@arabianalwaha.com
                </a>
              </li>
              <li>{t.location.split(',')[0]}</li>
              <li>{t.emergencySupport}</li>
            </ul>
          </div>
        </div>

        {/* Social Media */}
        <Separator className="my-8 bg-primary-foreground/20" />
        <div className="flex flex-col items-center text-center">
          <h4 className="font-semibold mb-6 text-accent text-lg">
            {language === 'ar' ? 'تابعنا على وسائل التواصل الاجتماعي' : 'Follow Us on Social Media'}
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 items-start justify-items-center">
            {/* Instagram with QR */}
            <a
              href="https://www.instagram.com/arabianalwahaksa"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-3 group"
              aria-label="Instagram @arabianalwahaksa"
            >
              <div className="flex items-center gap-2 text-primary-foreground/90 group-hover:text-accent transition-colors">
                <Instagram className="w-5 h-5" />
                <span className="font-medium">@arabianalwahaksa</span>
              </div>
              <div className="bg-white p-2 rounded-lg shadow-soft">
                <QRCode
                  value="https://www.instagram.com/arabianalwahaksa"
                  size={128}
                  level="M"
                  fgColor="#d62976"
                  bgColor="#ffffff"
                />
              </div>
            </a>

            {/* Facebook with QR */}
            <a
              href="https://www.facebook.com/share/1E4oGVKLvK/?mibextid=wwXIfr"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-3 group"
              aria-label={language === 'ar' ? 'صفحة الفيسبوك' : 'Facebook page'}
            >
              <div className="flex items-center gap-2 text-primary-foreground/90 group-hover:text-accent transition-colors">
                <Facebook className="w-5 h-5" />
                <span className="font-medium">{language === 'ar' ? 'فيسبوك' : 'Facebook'}</span>
              </div>
              <div className="bg-white p-2 rounded-lg shadow-soft">
                <QRCode
                  value="https://www.facebook.com/share/1E4oGVKLvK/?mibextid=wwXIfr"
                  size={128}
                  bgColor="#ffffff"
                  fgColor="#1877F2"
                  level="M"
                />
              </div>
            </a>

            {/* LinkedIn with QR */}
            <a
              href="https://www.linkedin.com/company/arabian-al-waha-contracting-industrial-services/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-3 group"
              aria-label={language === 'ar' ? 'صفحة لينكد إن' : 'LinkedIn page'}
            >
              <div className="flex items-center gap-2 text-primary-foreground/90 group-hover:text-accent transition-colors">
                <Linkedin className="w-5 h-5" />
                <span className="font-medium">{language === 'ar' ? 'لينكد إن' : 'LinkedIn'}</span>
              </div>
              <div className="bg-white p-2 rounded-lg shadow-soft">
                <QRCode
                  value="https://www.linkedin.com/company/arabian-al-waha-contracting-industrial-services/"
                  size={128}
                  bgColor="#ffffff"
                  fgColor="#0A66C2"
                  level="M"
                />
              </div>
            </a>

            {/* YouTube with QR */}
            <a
              href="https://youtube.com/@arabianalwaha-ksa?si=NUJFiW57otnGpTnB"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-3 group"
              aria-label={language === 'ar' ? 'قناة يوتيوب' : 'YouTube channel'}
            >
              <div className="flex items-center gap-2 text-primary-foreground/90 group-hover:text-accent transition-colors">
                <Youtube className="w-5 h-5" />
                <span className="font-medium">{language === 'ar' ? 'يوتيوب' : 'YouTube'}</span>
              </div>
              <div className="bg-white p-2 rounded-lg shadow-soft">
                <QRCode
                  value="https://youtube.com/@arabianalwaha-ksa?si=NUJFiW57otnGpTnB"
                  size={128}
                  bgColor="#ffffff"
                  fgColor="#FF0000"
                  level="M"
                />
              </div>
            </a>

            {/* Website QR */}
            <a
              href="https://arabianalwaha.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-3 group"
              aria-label={language === 'ar' ? 'الموقع الإلكتروني' : 'Website'}
            >
              <div className="flex items-center gap-2 text-primary-foreground/90 group-hover:text-accent transition-colors">
                <Globe className="w-5 h-5" />
                <span className="font-medium">arabianalwaha.com</span>
              </div>
              <div className="bg-white p-2 rounded-lg shadow-soft">
                <QRCode
                  value="https://arabianalwaha.com"
                  size={128}
                  bgColor="#ffffff"
                  fgColor="#1a3c2a"
                  level="M"
                />
              </div>
            </a>

            {/* WhatsApp Channel QR */}
            <div className="flex flex-col items-center gap-3">
              <div className="flex items-center gap-2 text-primary-foreground/90">
                <MessageCircle className="w-5 h-5" />
                <span className="font-medium">
                  {language === 'ar' ? 'قناة واتساب' : 'WhatsApp Channel'}
                </span>
              </div>
              <div className="bg-white p-2 rounded-lg shadow-soft">
                <img
                  src={whatsappChannelQr}
                  alt={language === 'ar' ? 'رمز QR لقناة واتساب' : 'WhatsApp channel QR code'}
                  className="w-32 h-32 object-contain"
                  loading="lazy"
                  width="128"
                  height="128"
                />
              </div>
            </div>
          </div>
        </div>

        <Separator className="my-8 bg-primary-foreground/20" />

        <div className="flex flex-col md:flex-row justify-between items-center gap-3 text-sm text-primary-foreground/70">
          <p className="text-center md:text-left">
            &copy; {new Date().getFullYear()}{" "}
            <span className="font-semibold text-primary-foreground">
              Arabian Al Waha Est.<sup aria-label="Trademark" className="ms-0.5 text-[0.6em] align-super">™</sup>
            </span>{" "}
            {language === "ar"
              ? "جميع الحقوق محفوظة. الجبيل، المملكة العربية السعودية."
              : "All rights reserved. Jubail, Kingdom of Saudi Arabia."}
          </p>
          <div className="flex space-x-6 mt-2 md:mt-0">
            <Link to="/privacy-policy" className="hover:text-primary-foreground transition-colors min-h-[44px] flex items-center py-2">{t.privacyPolicy}</Link>
            <Link to="/terms-of-service" className="hover:text-primary-foreground transition-colors min-h-[44px] flex items-center py-2">{t.termsOfService}</Link>
            <Link to="/quality-standards" className="hover:text-primary-foreground transition-colors min-h-[44px] flex items-center py-2">{t.qualityStandards}</Link>
          </div>
        </div>

        {/* Strong copyright + content-protection notice */}
        <div className="mt-6 pt-6 border-t border-primary-foreground/10 text-center text-xs text-primary-foreground/60 leading-relaxed max-w-3xl mx-auto">
          {language === "ar" ? (
            <p>
              جميع المحتويات والشعارات والصور والنصوص الموجودة على هذا الموقع
              مملوكة حصريًا لـ{" "}
              <span className="font-semibold text-primary-foreground/80">
                مؤسسة العربية الواحة<sup aria-label="علامة تجارية" className="ms-0.5 text-[0.6em] align-super">™</sup>
              </span>
              . يُمنع منعًا باتًا نسخ أو إعادة إنتاج أو توزيع أو تعديل أي جزء من
              هذا الموقع دون إذن خطي مسبق. أي استخدام غير مصرّح به يعرّض صاحبه
              للمساءلة القانونية.
            </p>
          ) : (
            <p>
              All content, logos, images, and text on this website are the
              exclusive property of{" "}
              <span className="font-semibold text-primary-foreground/80">
                Arabian Al Waha Est.<sup aria-label="Trademark" className="ms-0.5 text-[0.6em] align-super">™</sup>
              </span>{" "}
              No part of this site may be copied, reproduced, distributed, or
              modified — in whole or in part — without prior written permission.
              Unauthorized use is strictly prohibited and may result in legal
              action.
            </p>
          )}
        </div>
      </div>
    </footer>
  );
};

export default Footer;